import { HTTPError } from 'ky';
import { supportOwnerOf, supportSessionOf } from '@/shared/api/supportBearer';
import type { SupportSessionApi } from '@/shared/api/supportSessionEndpoints';
import type { StartSupportSessionRequest, SupportSessionAccessResponse } from '@/shared/api/types';
import { supportEndReason, SupportStartMismatchError } from './supportFailure';
import type { SupportEndReason, SupportNotice } from './supportNotices';
import { createSupportRenewal, type ActiveSupportSession } from './supportRenewal';
import { resumeSupportSession, viewOf } from './supportResume';
import { supportSessionStorage } from './supportSessionStorage';

interface SupportSessionOptions {
  api: () => SupportSessionApi;
  /** Benutzer und Workspace der Heimatsitzung; `null` ohne Anmeldung. */
  homeKey: () => string | null;
  onChange: () => void;
  notify: (notice: SupportNotice) => void;
}

/**
 * Die Support-Sitzung dieses Tabs. Das Token liegt nur hier im Speicher; Beginn, Neuausstellung
 * und Ende laufen über die Heimatsitzung. Jede Änderung meldet `onChange`, damit der Auth-Store
 * die wirksame Identität neu bestimmt.
 */
export function createSupportSession({ api, homeKey, onChange, notify }: SupportSessionOptions) {
  let active: ActiveSupportSession | null = null;
  let expiry: ReturnType<typeof setTimeout> | undefined;
  const renewal = createSupportRenewal({ api, active: () => active, onRenewed: onChange, finish });

  function release(): ActiveSupportSession | null {
    const released = active;
    active = null;
    renewal.reset();
    clearTimeout(expiry);
    supportSessionStorage.write(null);
    return released;
  }

  function finish(reason: SupportEndReason, sessionId: string) {
    if (active?.view.sessionId !== sessionId) return;
    const ended = release();
    notify({ reason, workspaceName: ended?.view.workspaceName ?? null });
    onChange();
  }

  /** Übernimmt eine Antwort des Servers nur, wenn sie zur Heimatsitzung dieses Tabs gehört. */
  function adopt(access: SupportSessionAccessResponse): boolean {
    const owner = homeKey();
    if (
      !owner ||
      supportSessionOf(access.accessToken) !== access.sessionId ||
      supportOwnerOf(access.accessToken) !== owner
    ) {
      return false;
    }
    release();
    active = { view: viewOf(access), token: access.accessToken, owner };
    supportSessionStorage.write(access.sessionId);
    const remaining = Date.parse(access.expiresAt) - Date.now();
    expiry = setTimeout(() => finish('expired', access.sessionId), Math.max(0, remaining));
    onChange();
    return true;
  }

  /** `true` nur, wenn der Server das Ende bestätigt hat; sonst steht der Grund im Protokoll. */
  const endAtServer = (sessionId: string): Promise<boolean> =>
    api()
      .end(sessionId)
      .then(
        () => true,
        (error: unknown) => {
          console.warn('[support] server end failed', {
            status: error instanceof HTTPError ? error.response.status : 0,
            name: error instanceof Error ? error.name : typeof error,
          });
          return false;
        },
      );

  return {
    tokens: renewal.tokens,
    view: () => active?.view ?? null,
    token: () => active?.token ?? null,
    belongsTo: (key: string | null) => active !== null && active.owner === key,
    needsResume: () => active === null && supportSessionStorage.read() !== null,

    /** Eine begonnene, aber fremde Sitzung endet sofort wieder; der Fehler sagt, wie es ausging. */
    async start(request: StartSupportSessionRequest): Promise<void> {
      const access = await api().start(request);
      if (adopt(access)) return;
      const ended = await endAtServer(access.sessionId);
      throw new SupportStartMismatchError(access.workspaceName, ended ? null : access.expiresAt);
    },

    /** Beenden aus Banner oder Umschalter. Beendet heißt: vom Server bestätigt. */
    async end(): Promise<void> {
      const session = active;
      if (!session) return;
      let reason: SupportEndReason = 'ended';
      try {
        await api().end(session.view.sessionId);
      } catch (error) {
        const refused = await supportEndReason(error, session.view.expiresAt);
        if (refused === null) throw error;
        reason = refused;
      }
      finish(reason, session.view.sessionId);
    },

    resume: (): Promise<void> =>
      active
        ? Promise.resolve()
        : resumeSupportSession({ api: api(), adopt, signedIn: () => homeKey() !== null, notify }),

    /**
     * Endet mit der eigenen Abmeldung oder Neuanmeldung, solange die Heimatsitzung noch gilt.
     * `signOut` meldet danach die Heimatsitzung ab, und das beendet beim Server jede Support-Sitzung
     * des Kontos: den Ablauf nennt der Hinweis nur, wenn weder Ende noch Abmeldung bestätigt sind.
     */
    async close(signOut?: () => Promise<boolean>): Promise<void> {
      const closed = release();
      const ended = closed !== null && (await endAtServer(closed.view.sessionId));
      let signedOut = false;
      try {
        signedOut = (await signOut?.()) ?? false;
      } finally {
        if (closed) {
          notify({
            reason: 'signed-out',
            workspaceName: closed.view.workspaceName,
            ...(ended || signedOut ? {} : { openUntil: closed.view.expiresAt }),
          });
        }
      }
    },

    /** Endet, weil die Heimatsitzung schon fort ist; ein anderes Konto kann sie nicht beenden. */
    forget(reason: SupportEndReason): void {
      const ended = release();
      if (!ended) return;
      const openUntil = reason === 'home-changed' ? { openUntil: ended.view.expiresAt } : {};
      notify({ reason, workspaceName: ended.view.workspaceName, ...openUntil });
    },

    /** Der Tab arbeitet ohne die Sitzung weiter; beim Server läuft sie bis zu ihrem Ablauf. */
    abandonResume: (): void => supportSessionStorage.write(null),
  };
}

export type SupportSession = ReturnType<typeof createSupportSession>;
